import axios, { type AxiosHeaders, type InternalAxiosRequestConfig } from 'axios';
import { API_BASE_URL } from '../config/api';
import { authService } from './authService';

const isDev = import.meta.env.DEV;

function redactHeaders(headers: InternalAxiosRequestConfig['headers']): Record<string, unknown> {
  if (!headers) return {};
  const raw =
    typeof (headers as AxiosHeaders).toJSON === 'function'
      ? (headers as AxiosHeaders).toJSON()
      : { ...(headers as Record<string, unknown>) };
  const copy: Record<string, unknown> = { ...raw };
  if (copy.Authorization) copy.Authorization = 'Bearer [REDACTED]';
  if (copy.authorization) copy.authorization = 'Bearer [REDACTED]';
  return copy;
}

// Create axios instance
const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    'x-client-source': 'app-web',
  },
});

// Add request interceptor
axiosInstance.interceptors.request.use(
  (config) => {
    if (isDev) {
      console.log('[axios] request', config.method?.toUpperCase(), config.url);
      console.log('[axios] headers', redactHeaders(config.headers));
    }
    const token = authService.getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    if (isDev) {
      console.error('[axios] request error', error);
    }
    return Promise.reject(error);
  }
);

// Add response interceptor
axiosInstance.interceptors.response.use(
  (response) => {
    if (isDev) {
      console.log('[axios] response', response.status, response.config.url);
    }
    return response;
  },
  async (error) => {
    if (isDev) {
      console.error('[axios] response error', error.response?.status, error.config?.url);
    }
    const originalRequest = error.config;

    // Only handle 401 Unauthorized - not other errors like 404
    // If error is 401 and we haven't tried to refresh token yet
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      // Don't redirect for intake-form routes (they're meant to be public when API supports them)
      const isPublicRoute =
        originalRequest.url?.includes('/intake-forms') &&
        !originalRequest.url?.includes('/submit');

      if (!isPublicRoute) {
        // Don't try to logout if the request was already a logout request
        if (originalRequest.url && !originalRequest.url.includes('/client-auth/signout')) {
          // Clear token from localStorage
          localStorage.removeItem('token');
          authService.token = null;

          // Redirect to login if not already there
          if (
            !window.location.pathname.includes('/login') &&
            !window.location.pathname.includes('/signup')
          ) {
            window.location.href = '/login';
          }
        }
      }
    }

    return Promise.reject(error);
  }
);

export default axiosInstance;
